import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import extractQuestions from 'shared/utils/extractQuestions';

import { FormattedMessage, useIntl } from 'react-intl';
import { SortableTreeWithoutDndContext as SortableTree, changeNodeAtPath, removeNodeAtPath } from '@nosferatu500/react-sortable-tree';

import { Spin, Layout, Form } from 'antd';
import Drawer from 'components/Drawer';
import Modal from 'components/Modal';

import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import { DELETE_QUESTION_WITH_RESPONSES } from 'shared/constants/permissions';
import globalMessages from 'translations/messages';
import ImportContentForm from './ImportContentForm';
import EditContentForm from './EditContentForm';
import DragToAddContent, { NODE_DND_TYPE } from './DragToAddContent';
import nodeContentRenderer from './SurveyContentTreeNodeItem';
import { EditIntro, EditClosing } from './EditIntroAndClosing';

import query from './query.gql';
import messages from './messages';
// Custom package does not export it so copied locally
import './react-sortable-tree.css';
import './edit-structure.less';

const REMOVE_CONTENT = gql`
  mutation ($id: ID!, $force: Boolean) {
    removeSurveyContent(id: $id, force: $force) { status message responseCount }
  }
`;

const MOVE_CONTENT = gql`
  mutation ($id: ID!, $surveyId: ID, $parentId: ID, $position: Int!) {
    moveSurveyContent(id: $id, surveyId: $surveyId, parentId: $parentId, position: $position) { id }
  }
`;

const TOGGLE_CONTENT = gql`
  mutation ($id: ID! $active: Boolean) {
    toggleSurveyContent(id: $id, active: $active) { id }
  }
`;

const CREATE_PAGE_BREAK = gql`
  mutation ($position: Int!, $parentId: ID!) {
    createPageBreak(position: $position, parentId: $parentId) { id }
  }
`;

function toTreeData(root, expandedIds) {
  const treeData = root.children.map(item => ({
    ...item,
    expanded: !!expandedIds.includes(item.id),
    children: item.block && toTreeData(item.block, expandedIds),
  }));

  return treeData;
}

const EditContent = ({ customerId, surveyId }) => {
  const { formatMessage: $t } = useIntl();
  const { refetch, loading, data: { survey } = {} } = useQuery(query, {
    variables: { surveyId },
    fetchPolicy: 'network-only',
  });
  const [removeContentMutation] = useMutation(REMOVE_CONTENT);
  const [moveContent] = useMutation(MOVE_CONTENT);
  const [createPageBreak] = useMutation(CREATE_PAGE_BREAK);
  const [toggleContent] = useMutation(TOGGLE_CONTENT);
  const { hasDataSourcePermission } = useUser();
  const dataSourceId = survey?.languageParent?.dataSource?.id || survey?.website?.dataSource?.id;
  const canForceDelete = hasDataSourcePermission(dataSourceId, DELETE_QUESTION_WITH_RESPONSES);

  const [treeData, setTreeData] = useState([]);
  const getNodeKey = ({ node }) => node.id || 'new';

  useEffect(() => {
    if (!survey) { return; }
    const expandedIds = treeData.filter(node => node.expanded).map(node => node.id);
    const data = toTreeData(survey.root, expandedIds);
    setTreeData(data);
  }, [survey]);

  const [importContentForm] = Form.useForm();
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const openImportDrawer = () => setImportDrawerOpen(true);
  const closeImportDrawer = () => setImportDrawerOpen(false);
  const cancelImport = () => {
    importContentForm.resetFields();
    closeImportDrawer();
  };
  const onImport = () => setImporting(true);
  const onImportDone = () => {
    setImporting(false);
    closeImportDrawer();
    refetch();
  };

  const editContentRef = useRef();
  const [selectedNode, setSelectedNode] = useState();
  const [saving, setSaving] = useState(false);
  const { modal, message } = useGlobalContext();

  const onEditContent = (node, path) => setSelectedNode({ ...node, path });

  const saveEdit = async () => {
    if (editContentRef.current) {
      const valid = await editContentRef.current.save();
      setSaving(valid);
    }
  };
  const onEditDone = (error) => {
    if (error) {
      message.error($t(messages.contentSaveError));
      if (!selectedNode.id) {
        const data = removeNodeAtPath({ treeData, path: selectedNode.path, getNodeKey });
        setTreeData(data);
      }
    } else {
      message.success($t(messages.contentSaveSuccess));
      refetch();
    }

    setSaving(false);
    setSelectedNode(null);
  };

  const cancelEdit = () => {
    if (editContentRef.current) {
      editContentRef.current.cancel();
      if (!selectedNode.id) {
        const data = removeNodeAtPath({ treeData, path: selectedNode.path, getNodeKey });
        setTreeData(data);
      }
    }
    setSelectedNode(null);
  };

  const removeContent = async (id, force = false) => {
    const { data: { removeSurveyContent } } = await removeContentMutation({ variables: { id, force } });
    return removeSurveyContent;
  };

  const showHasResponsesModal = (id, path, node, responseCount) => {
    modal.confirm({
      title: node?.question
        ? $t(messages.questionHasResponses, { responses: responseCount })
        : $t(messages.blockHasQuestionsWithResponses),
      okText: $t(messages.delete),
      okType: 'danger',
      okButtonProps: canForceDelete ? undefined : { style: { display: 'none' } },
      cancelText: canForceDelete ? $t(globalMessages.cancel) : $t(globalMessages.ok),
      async onOk() {
        if (!canForceDelete) {
          return;
        }
        try {
          const result = await removeContent(id, true);
          if (result.status === 'ERROR') {
            message.error(result.message);
          } else if (result.status === 'SUCCESS') {
            setTreeData(removeNodeAtPath({ treeData, path, getNodeKey }));
          }
        } catch (e) {
          message.error(e.message);
        }
      },
    });
  };

  const onDeleteContent = async (id, path, node) => {
    modal.confirm({
      title: $t(messages.confirmDeleteContent),
      okText: $t(messages.delete),
      okType: 'danger',
      async onOk() {
        const result = await removeContent(id);
        if (result.status === 'HAS_RESPONSES') {
          showHasResponsesModal(id, path, node, result.responseCount);
        } else if (result.status === 'ERROR') {
          message.error(result.message);
        } else {
          setTreeData(removeNodeAtPath({ treeData, path, getNodeKey }));
        }
      },
    });
  };

  const onToggleActive = async (content, path) => {
    const { id, active: isActive } = content;
    const active = !isActive;

    toggleContent({ variables: { id, active } });

    const data = changeNodeAtPath({
      treeData,
      path,
      newNode: ({ node }) => ({ ...node, active }),
      getNodeKey,
    });

    setTreeData(data);
  };

  const onMoveContent = ({ treeData: data, node, nextParentNode, nextPath }) => {
    const position = nextParentNode
      ? nextParentNode.children.indexOf(node)
      : data.indexOf(node);
    const parentId = (nextParentNode && nextParentNode.block.id) || survey.root.id;

    if (node.id) {
      moveContent({ variables: { id: node.id, position, parentId } });
    } else if (node.pageBreak) {
      createPageBreak({ variables: { position, parentId } });
    } else {
      setSelectedNode({
        ...node,
        path: nextPath,
        position,
        parentId,
      });
    }
  };

  // Only support depth of 1
  // root
  //  |_ Text
  //  |_ Question
  //  |_ Block
  //      |_ Question
  const canDrop = ({ node, nextParent }) => !nextParent || (nextParent.block && !node.block);
  const canHaveChildren = (node) => node.block;

  const getNodeProps = () => ({
    onEdit: onEditContent,
    onRemove: (nodeId, path, node) => onDeleteContent(nodeId, path, node),
    onToggleActive,
  });
  const getSurveyQuestions = (current) => (survey ? extractQuestions(survey.root.children, current) : []);

  const getEditTitle = (node) => {
    let type = 'Question';
    if (node.block) { type = 'Block'; }
    if (node.text) { type = 'Text block'; }
    return <FormattedMessage {...messages.editContent} values={{ type }} />;
  };

  return (
    <>
      <Modal
        title={selectedNode && getEditTitle(selectedNode)}
        open={!!selectedNode}
        maskClosable={false}
        okText={<FormattedMessage {...messages.save} />}
        onOk={saveEdit}
        onCancel={cancelEdit}
        confirmLoading={saving}
        fullWidth
        destroyOnClose
      >
        <EditContentForm
          ref={editContentRef}
          content={selectedNode}
          onDone={onEditDone}
          surveyQuestions={getSurveyQuestions(selectedNode?.id)}
          surveyId={surveyId}
        />
      </Modal>
      <Drawer
        title={<FormattedMessage {...messages.import} />}
        open={importDrawerOpen}
        onClose={closeImportDrawer}
        onCancel={cancelImport}
        onOk={importContentForm.submit}
        confirmLoading={importing}
      >
        <ImportContentForm
          customerId={customerId}
          surveyId={surveyId}
          targetBlockId={survey && survey.root.id}
          form={importContentForm}
          onSubmit={onImport}
          onDone={onImportDone}
        />
      </Drawer>
      <Spin spinning={loading} size="large">
        <Layout>
          <Layout.Sider theme="light">
            <DragToAddContent onImport={openImportDrawer} />
          </Layout.Sider>
          <Layout.Content className="edit-survey-content">
            <EditIntro
              settingsId={survey?.settings?.id}
              showOnSeparatePage={survey?.settings?.showWelcomePage}
              content={survey?.settings?.welcomePageContent}
            />

            <SortableTree
              className="survey-content-tree"
              treeData={treeData}
              getNodeKey={getNodeKey}
              canDrop={canDrop}
              canNodeHaveChildren={canHaveChildren}
              generateNodeProps={getNodeProps}
              onChange={data => setTreeData(data)}
              onMoveNode={onMoveContent}
              dndType={NODE_DND_TYPE}
              theme={{
                nodeContentRenderer,
                scaffoldBlockPxWidth: 33,
              }}
            />

            <EditClosing
              settingsId={survey?.settings?.id}
              content={survey?.settings?.thankYouPageContent}
            />
          </Layout.Content>
        </Layout>
      </Spin>
    </>
  );
};

export default EditContent;
